import { createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import type { DateTime } from 'luxon';
import moment, { Moment } from 'moment';

import { selectedUnitSel } from '@modules/councilUnits/duck/selectors';
import { UserOrganizationInfo } from '@modules/user/duck/types';
import { organizationPositionsSel } from '@shared/duck/selectors/general.selectors';
import { unitInfoSel } from '@unit/duck/selectorsTyped';

import { selectedRangeDateSel } from './selectors';
import { FetchUnitEventsRequest } from './types';

/**
 * `unitId` reaches us as a string, so a falsy check lets `'0'` through
 * (`Boolean('0') === true`) and the ESB rejects `unitId: 0` with a 400.
 */
const toUnitId = (value: unknown): number | null => {
  const unitId = Number(value);

  return Number.isInteger(unitId) && unitId > 0 ? unitId : null;
};

export const calendarRequestsSel = createSelector(
  (
    _state: unknown,
    props: {
      selectedDate: string | Moment;
      rangeDate?: null | DateTime[];
    },
  ) => props.selectedDate,
  organizationPositionsSel,
  // These are for councils
  selectedUnitSel,
  unitInfoSel,
  //------------------
  selectedRangeDateSel,
  (
    selectedDate,
    organizationPositions,
    councilSelectedUnit,
    currentUnitInfo,
    rangeDate,
  ) => {
    const hasRangeDate = !!rangeDate?.length;
    const [startRangeDate, endRangeDate] = rangeDate || [];
    const validOrg: { unitId: number }[] = organizationPositions
      .map((org: UserOrganizationInfo) => toUnitId(org.unitId))
      .filter((unitId: number | null): unitId is number => unitId !== null)
      .map((unitId: number) => ({ unitId }));

    // Council is using a unit
    const councilUnitId = toUnitId(currentUnitInfo?.sbOrganizationId);
    if (
      councilSelectedUnit?.organizationGuid ===
        currentUnitInfo?.organizationGuid &&
      councilUnitId
    ) {
      validOrg.push({ unitId: councilUnitId });
    }

    if (!validOrg.length) {
      return [];
    }

    return _.uniqBy(validOrg, 'unitId').map(org => {
      const { unitId } = org;

      const fromDate = hasRangeDate
        ? startRangeDate.toFormat('yyyy-MM-dd')
        : moment(selectedDate)
            .startOf('month')
            .subtract(1, 'week')
            .format('Y-MM-DD');
      const toDate = hasRangeDate
        ? endRangeDate.toFormat('yyyy-MM-dd')
        : moment(selectedDate).endOf('month').add(1, 'week').format('Y-MM-DD');

      const payload: FetchUnitEventsRequest = {
        toDate,
        fromDate,
        unitId,
        showDLEvents: true,
      };

      return payload;
    });
  },
);
